import { Button, LoadingArea, Spinner } from '@consigli/facade';
import { useConvertLayoutResultToPdfMutation, useProjectId, useLayoutId } from '@consigli/hooks';
import { FC, useCallback, useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';

type HTMLViewerEditorProps = {
  fileUrl: string;
  onClose?: () => void;
  resultId?: string;
};

export const HTMLViewerEditor: FC<HTMLViewerEditorProps> = ({ fileUrl, onClose, resultId }) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const isUpdatingRef = useRef(false);

  const projectId = useProjectId();
  const layoutId = useLayoutId();
  const [convertToPdf] = useConvertLayoutResultToPdfMutation();

  useEffect(() => {
    const loadHTML = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to load HTML: ${response.statusText}`);
        }
        const content = await response.text();
        setHtmlContent(content);
      } catch (error) {
        console.error('Error loading HTML:', error);
        toast.error(`Failed to load HTML content: ${error}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (fileUrl) {
      loadHTML();
    }
  }, [fileUrl]);

  // Set initial content in the editable div (only once after loading)
  const hasSetInitialContentRef = useRef(false);

  useEffect(() => {
    if (htmlContent && !hasSetInitialContentRef.current && !isUpdatingRef.current) {
      const setContent = () => {
        if (previewRef.current) {
          isUpdatingRef.current = true;
          try {
            previewRef.current.innerHTML = htmlContent;
            hasSetInitialContentRef.current = true; // Mark as set
          } catch (innerError) {
            console.error('Error setting innerHTML:', innerError);
          }
          isUpdatingRef.current = false;
        } else {
          setTimeout(setContent, 10);
        }
      };

      setTimeout(setContent, 0);
    }
  }, [htmlContent]);

  // Handle content changes from the editable preview
  const handleContentChange = useCallback(() => {
    if (previewRef.current && !isUpdatingRef.current) {
      const newContent = previewRef.current.innerHTML;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setHtmlContent(newContent);
        setHasUnsavedChanges(true);
      }, 300);
    }
  }, []);

  const handleSaveAsPDF = useCallback(async () => {
    if (!resultId) {
      toast.error('Cannot convert to PDF: Result ID not available');
      return;
    }

    try {
      setIsSaving(true);

      await convertToPdf({
        projectId,
        layoutId,
        resultId,
        htmlContent,
      }).unwrap();

      if (onClose) {
        onClose();
      }

      toast.success('PDF conversion completed successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to convert HTML to PDF. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [htmlContent, convertToPdf, projectId, layoutId, resultId, onClose]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return <LoadingArea />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600 font-medium">• Unsaved changes</span>
          )}
        </div>
        <div className="flex items-center space-x-2 mr-12">
          <Button primary rounded onClick={handleSaveAsPDF} disabled={isSaving}>
            <div className="flex items-center space-x-2">
              {isSaving && <Spinner size="xsmall" />}
              <span>{isSaving ? 'Generating...' : 'Save as PDF'}</span>
            </div>
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        <div
          ref={previewRef}
          className="w-full h-full p-8 max-w-4xl mx-auto outline-none"
          style={{ minHeight: '100%' }}
          contentEditable
          suppressContentEditableWarning={true}
          onInput={handleContentChange}
          spellCheck={true}
        />
      </div>
    </div>
  );
};
